import { useCallback, useContext } from 'react';
import BoardsContext from '../../../../../../contexts/BoardsContext';
import sectionTypeCheckers from '../../../../../../components/PerformanceBoards/PerformanceBoardSlideOut/sectionTypeCheckers';
import isPerformanceBoard from '../../../../../../isPerformanceBoard';

const useGetFieldPerformanceBoardUsage = () => {
  const { boards } = useContext(BoardsContext);

  const getPerformanceBoardUsage = useCallback(
    (field: string, dataset: string): PerformanceBoardTypes.Board[] => {
      const performanceBoards = boards.filter(
        (b): b is PerformanceBoardTypes.Board => isPerformanceBoard(b),
      );

      const matches = performanceBoards.filter((b) => {
        if (b.dataType !== dataset) return false;

        const hasInColumns = b.columnOrder?.some((c) => c.field === field);
        const hasInSort = b.defaultSort?.field === field;

        const hasInSizeStrategy = (() => {
          const s = b.columnSizeStrategy;
          if (!s) return false;
          if (s.type === 'fitGridWidth') {
            return s.columnLimits?.some((cl) => cl.field === field) || false;
          }
          if (s.type === 'fitCellContents') {
            return s.fields?.includes(field) || false;
          }
          return false;
        })();

        const hasInSlideout = b.slideout.some((section) => {
          if (
            sectionTypeCheckers.isPropertiesSection(section) ||
            sectionTypeCheckers.isRelatedDocumentsSection(section) ||
            sectionTypeCheckers.isGridSection(section) ||
            sectionTypeCheckers.isTargetProgressSection(section)
          ) {
            const fields = section.fields;
            return Array.isArray(fields) && fields.includes(field);
          } else if (sectionTypeCheckers.isPerformanceSection(section)) {
            const performanceField = section.performanceField;
            return performanceField === field;
          }
          return false;
        });

        return hasInColumns || hasInSort || hasInSlideout || hasInSizeStrategy;
      });

      // Preserve original order
      return matches;
    },
    [boards],
  );

  return getPerformanceBoardUsage;
};

export default useGetFieldPerformanceBoardUsage;
